/** Stable ids for quest editor / page sections. Spot checkpoints use `spot:N`, page photos use `photo:N`, custom blocks use `block:N`. */
import { alignPageBlocksWithListOrder, blockSectionId, parseBlockSectionId, type QuestPageBlock } from "./quest-page-blocks.ts";
import { isRouteEndpointCheckpoint } from "./route-trace.ts";

export type QuestSectionId = string;

export const QUEST_SECTION_TITLE = "title";
/** Legacy page_order id; cover is edited only under the title section now. */
export const QUEST_SECTION_COVER = "cover";
export const QUEST_SECTION_INTRO = "intro";
export const QUEST_SECTION_MAP = "map";
export const QUEST_SECTION_ROUTE = "route";
export const QUEST_SECTION_SETTINGS = "settings";
export const QUEST_SECTION_SPOTS = "spots";
export const QUEST_SECTION_STORY = "story";
export const QUEST_SECTION_MANNERS = "manners";
/** Palette tile id for adding a new inline photo block (not stored in page_order). */
export const QUEST_SECTION_PHOTO = "photo";

export const SPOT_SECTION_PREFIX = "spot:";
export const PHOTO_SECTION_PREFIX = "photo:";

export type { PageBlockKind } from "./quest-page-blocks.ts";
export { blockSectionId, parseBlockSectionId, PALETTE_PAGE_BLOCK_KINDS } from "./quest-page-blocks.ts";

export function spotSectionId(index: number): QuestSectionId {
  return `${SPOT_SECTION_PREFIX}${index}`;
}

export function photoSectionId(index: number): QuestSectionId {
  return `${PHOTO_SECTION_PREFIX}${index}`;
}

export function parseSpotSectionId(id: QuestSectionId): number | null {
  if (!id.startsWith(SPOT_SECTION_PREFIX)) return null;
  const n = Number(id.slice(SPOT_SECTION_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export function parsePhotoSectionId(id: QuestSectionId): number | null {
  if (!id.startsWith(PHOTO_SECTION_PREFIX)) return null;
  const n = Number(id.slice(PHOTO_SECTION_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export interface QuestOrderContext {
  quest_type?: "route_trace" | "spot_trace" | null;
  checkpointCount: number;
  photoCount: number;
  blockCount: number;
  /** Checkpoint kinds aligned with indices; used to hide Start/Finish rows on GPS Art. */
  checkpointKinds?: (string | null | undefined)[];
}

/** Default section order for the three-column editor (also used when persisting first save). */
export function defaultEditorSectionOrder(ctx: QuestOrderContext): QuestSectionId[] {
  const spots = Array.from({ length: ctx.checkpointCount }, (_, i) => spotSectionId(i));
  if (ctx.quest_type === "spot_trace") {
    return [QUEST_SECTION_TITLE, QUEST_SECTION_MAP, ...spots];
  }
  return [QUEST_SECTION_TITLE, QUEST_SECTION_ROUTE, ...missionSpotSectionIds(ctx)];
}

/** GPS Art: title first, route second; other sections keep relative order after migration. */
export function normalizeRouteTraceCoreSectionOrder(order: QuestSectionId[]): QuestSectionId[] {
  const migrated = migrateRouteTraceSectionOrder(order);
  const rest = migrated.filter((id) => id !== QUEST_SECTION_TITLE && id !== QUEST_SECTION_ROUTE);
  const out: QuestSectionId[] = [];
  if (migrated.includes(QUEST_SECTION_TITLE)) out.push(QUEST_SECTION_TITLE);
  if (migrated.includes(QUEST_SECTION_ROUTE)) out.push(QUEST_SECTION_ROUTE);
  out.push(...rest);
  return out;
}

/** `spot:N` section ids for mission checkpoints (GPS Art skips Start/Finish). */
export function missionSpotSectionIds(ctx: QuestOrderContext): QuestSectionId[] {
  const out: QuestSectionId[] = [];
  for (let i = 0; i < ctx.checkpointCount; i++) {
    if (ctx.quest_type === "route_trace" && isRouteEndpointCheckpoint(ctx.checkpointKinds?.[i])) continue;
    out.push(spotSectionId(i));
  }
  return out;
}

export function shouldShowSpotSectionInEditor(
  ctx: QuestOrderContext,
  checkpointIndex: number,
): boolean {
  if (ctx.quest_type !== "route_trace") return true;
  return !isRouteEndpointCheckpoint(ctx.checkpointKinds?.[checkpointIndex]);
}

/** Collapse legacy GPS Art map/settings/spots keys into a single route card. */
export function migrateRouteTraceSectionOrder(order: QuestSectionId[]): QuestSectionId[] {
  const out: QuestSectionId[] = [];
  let routeSeen = false;
  for (const id of order) {
    if (id === QUEST_SECTION_SETTINGS || id === QUEST_SECTION_MAP || id === QUEST_SECTION_SPOTS) {
      if (!routeSeen) {
        out.push(QUEST_SECTION_ROUTE);
        routeSeen = true;
      }
      continue;
    }
    if (id === QUEST_SECTION_ROUTE) {
      if (!routeSeen) out.push(QUEST_SECTION_ROUTE);
      routeSeen = true;
      continue;
    }
    out.push(id);
  }
  if (!routeSeen) {
    const titleIdx = out.indexOf(QUEST_SECTION_TITLE);
    if (titleIdx >= 0) out.splice(titleIdx + 1, 0, QUEST_SECTION_ROUTE);
    else out.unshift(QUEST_SECTION_ROUTE);
  }
  return out;
}

/** Spot quests always show the route overview map in the editor preview when they have checkpoints. */
export function ensureSpotTraceMapSection(order: QuestSectionId[]): QuestSectionId[] {
  const rest = order.filter((id) => id !== QUEST_SECTION_MAP);
  const next = rest.slice();
  const titleIdx = next.indexOf(QUEST_SECTION_TITLE);
  if (titleIdx >= 0) next.splice(titleIdx + 1, 0, QUEST_SECTION_MAP);
  else next.unshift(QUEST_SECTION_MAP);
  return next;
}

/** Optional blocks the palette can add (not including spots or repeatable photos). */
export const PALETTE_SECTION_KINDS = [QUEST_SECTION_INTRO, QUEST_SECTION_STORY, QUEST_SECTION_MANNERS] as const;

export type PaletteSectionKind = (typeof PALETTE_SECTION_KINDS)[number];

export function isPaletteSection(id: QuestSectionId): id is PaletteSectionKind {
  return (PALETTE_SECTION_KINDS as readonly string[]).includes(id);
}

export function isRequiredSection(id: QuestSectionId, ctx: QuestOrderContext): boolean {
  if (id === QUEST_SECTION_TITLE) return true;
  if (ctx.quest_type === "spot_trace") {
    return id === QUEST_SECTION_MAP;
  }
  if (id === QUEST_SECTION_ROUTE) return true;
  if (id === QUEST_SECTION_SETTINGS) return ctx.quest_type !== "route_trace";
  return false;
}

/** Auto start/finish rows on GPS Art quests stay in the list but cannot be removed. */
export function isProtectedSpotSection(kind: string | null | undefined): boolean {
  return kind === "start" || kind === "finish";
}

export function canDeleteEditorSection(
  id: QuestSectionId,
  ctx: QuestOrderContext,
  checkpointKind: string | null | undefined,
): boolean {
  if (isRequiredSection(id, ctx)) return false;
  const spot = parseSpotSectionId(id);
  if (spot != null && isProtectedSpotSection(checkpointKind)) return false;
  return true;
}

export function normalizePageOrder(raw: unknown, ctx: QuestOrderContext): QuestSectionId[] | null {
  if (raw == null) return null;
  if (!Array.isArray(raw)) return null;
  const out: QuestSectionId[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== "string" || !item.trim()) continue;
    const id = item.trim();
    if (id === QUEST_SECTION_COVER) continue;
    if (seen.has(id)) continue;
    if (!isKnownSectionId(id, ctx)) continue;
    seen.add(id);
    out.push(id);
  }
  return out.length ? out : null;
}

function isKnownSectionId(id: QuestSectionId, ctx: QuestOrderContext): boolean {
  if (
    id === QUEST_SECTION_TITLE ||
    id === QUEST_SECTION_INTRO ||
    id === QUEST_SECTION_MAP ||
    id === QUEST_SECTION_ROUTE ||
    id === QUEST_SECTION_SETTINGS ||
    id === QUEST_SECTION_SPOTS ||
    id === QUEST_SECTION_STORY ||
    id === QUEST_SECTION_MANNERS
  ) {
    return true;
  }
  const spot = parseSpotSectionId(id);
  if (spot != null) return spot < ctx.checkpointCount;
  const photo = parsePhotoSectionId(id);
  if (photo != null) return photo < ctx.photoCount;
  const block = parseBlockSectionId(id);
  return block != null && block < ctx.blockCount;
}

/** Merge saved order with current quest shape (new spots/photos, removed ids). */
export function resolveEditorSectionOrder(
  saved: QuestSectionId[] | null | undefined,
  ctx: QuestOrderContext,
): QuestSectionId[] {
  const required = defaultEditorSectionOrder(ctx).filter((id) => isRequiredSection(id, ctx));
  const base = saved?.length ? [...saved] : defaultEditorSectionOrder(ctx);
  const out: QuestSectionId[] = [];
  const seen = new Set<string>();

  const push = (id: QuestSectionId) => {
    if (id === QUEST_SECTION_COVER) return;
    if (!isKnownSectionId(id, ctx) && parseSpotSectionId(id) == null && parsePhotoSectionId(id) == null && parseBlockSectionId(id) == null) return;
    if (parseSpotSectionId(id) != null && !isKnownSectionId(id, ctx)) return;
    if (parsePhotoSectionId(id) != null && !isKnownSectionId(id, ctx)) return;
    if (parseBlockSectionId(id) != null && !isKnownSectionId(id, ctx)) return;
    if (seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };

  for (const id of base) push(id);
  if (ctx.quest_type === "route_trace") {
    const migrated = migrateRouteTraceSectionOrder(out);
    out.length = 0;
    seen.clear();
    for (const id of migrated) push(id);
  }
  for (const id of required) {
    if (!seen.has(id)) out.push(id);
  }
  for (let i = 0; i < ctx.checkpointCount; i++) {
    if (!shouldShowSpotSectionInEditor(ctx, i)) continue;
    const id = spotSectionId(i);
    if (!seen.has(id)) {
      const titleIdx = out.indexOf(QUEST_SECTION_TITLE);
      if (titleIdx >= 0) out.splice(titleIdx + 1, 0, id);
      else out.push(id);
      seen.add(id);
    }
  }
  for (let i = 0; i < ctx.photoCount; i++) {
    const id = photoSectionId(i);
    if (!seen.has(id)) out.push(id);
  }
  for (let i = 0; i < ctx.blockCount; i++) {
    const id = blockSectionId(i);
    if (!seen.has(id)) out.push(id);
  }
  if (ctx.quest_type === "spot_trace" && ctx.checkpointCount > 0) {
    return ensureSpotTraceMapSection(out);
  }
  if (ctx.quest_type === "route_trace") {
    return normalizeRouteTraceCoreSectionOrder(
      out.filter((id) => {
        if (id === QUEST_SECTION_SETTINGS || id === QUEST_SECTION_MAP || id === QUEST_SECTION_SPOTS) return false;
        const spot = parseSpotSectionId(id);
        if (spot != null && !shouldShowSpotSectionInEditor(ctx, spot)) return false;
        return true;
      }),
    );
  }
  return out;
}

/** After reordering spot rows in the editor list, permute checkpoints and renumber spot ids. */
export function alignCheckpointsWithSpotListOrder<T>(
  order: QuestSectionId[],
  checkpoints: T[],
): { order: QuestSectionId[]; checkpoints: T[] } {
  const perm = order.map((id) => parseSpotSectionId(id)).filter((n): n is number => n != null);
  if (perm.length !== checkpoints.length) return { order, checkpoints };
  const nextCp = perm.map((i) => checkpoints[i]!);
  let spot = 0;
  const nextOrder = order.map((id) => (parseSpotSectionId(id) != null ? spotSectionId(spot++) : id));
  return { order: nextOrder, checkpoints: nextCp };
}

/** After reordering photo rows, permute photos and renumber photo:* ids. */
export function alignPagePhotosWithListOrder<T>(
  order: QuestSectionId[],
  photos: T[],
): { order: QuestSectionId[]; photos: T[] } {
  const perm = order.map((id) => parsePhotoSectionId(id)).filter((n): n is number => n != null);
  if (perm.length !== photos.length) return { order, photos };
  const nextPhotos = perm.map((i) => photos[i]!);
  let photo = 0;
  const nextOrder = order.map((id) => (parsePhotoSectionId(id) != null ? photoSectionId(photo++) : id));
  return { order: nextOrder, photos: nextPhotos };
}

export function alignListOrderWithSections<T, U>(
  order: QuestSectionId[],
  checkpoints: T[],
  photos: U[],
  blocks: QuestPageBlock[],
): { order: QuestSectionId[]; checkpoints: T[]; photos: U[]; blocks: QuestPageBlock[] } {
  const spots = alignCheckpointsWithSpotListOrder(order, checkpoints);
  const photosAligned = alignPagePhotosWithListOrder(spots.order, photos);
  const blocksAligned = alignPageBlocksWithListOrder(photosAligned.order, blocks);
  return {
    order: blocksAligned.order,
    checkpoints: spots.checkpoints,
    photos: photosAligned.photos,
    blocks: blocksAligned.blocks,
  };
}

export function reorderSectionOrder(order: QuestSectionId[], fromIndex: number, toIndex: number): QuestSectionId[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= order.length || toIndex >= order.length) {
    return order;
  }
  const next = order.slice();
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item!);
  return next;
}

/** Checkpoint order follows spot:* section order in the list. */
export function checkpointOrderFromSectionOrder(order: QuestSectionId[], count: number): number[] {
  const fromOrder = order.map((id) => parseSpotSectionId(id)).filter((n): n is number => n != null && n < count);
  const seen = new Set<number>();
  const out: number[] = [];
  for (const i of fromOrder) {
    if (seen.has(i)) continue;
    seen.add(i);
    out.push(i);
  }
  for (let i = 0; i < count; i++) {
    if (!seen.has(i)) out.push(i);
  }
  return out;
}

export function applyCheckpointPermutation<T>(items: T[], order: number[]): T[] {
  return order.map((i) => items[i]!).filter(Boolean);
}
